import { computed, type ComputedRef } from 'vue';
import { useQueuePosition } from './useQueuePosition';
import { useAgentsOnline } from './useAgentsOnline';
import { useWaitTimes } from './useWaitTimes';
import { useVoiceAiAvailability } from './useVoiceAiAvailability';
import type { ConnectOptionKind } from '@/common/bot/enum';
import type { OnlineAgentsCheck } from '@/common/api/models.pleased';

export interface UseConnectDataProps {
  showLiveChat: boolean;
  showVoiceChat: boolean;
  showOpenRequest: boolean;
  /** Landing channel picker always shows all three options (Live Chat = AI entry, not agent queue). */
  isLandingEntry?: boolean;
  selectedOption?: ConnectOptionKind;
}

export interface ConnectData {
  queuePosition: number;
  agentsOnline: OnlineAgentsCheck;
  waitTimes: {
    chat: string | null;
    call: string | null;
  };
  showLiveChat: boolean;
  showCall: boolean;
  showOpenRequest: boolean;
  isVoiceAiDisabled: boolean;
  isLoading: boolean;
}

/**
 * Composable that combines queue position, agents online, and wait times data
 * Uses TanStack Query hooks for fetching and caching
 *
 * @param props - Configuration for which options to show
 * @returns Combined connect data with loading state
 *
 * @example
 * ```ts
 * const props = computed(() => ({ showLiveChat: true, showVoiceChat: true, showOpenRequest: true }));
 * const connectData = useConnectData(props);
 * const showLiveChat = computed(() => connectData.value.showLiveChat);
 * ```
 */
export const useConnectData = (props: ComputedRef<UseConnectDataProps>) => {
  const queriesEnabled = computed(() => props.value.isLandingEntry !== true);
  const queryOptions = { enabled: queriesEnabled };

  const voiceAiQueryEnabled = computed(
    () => props.value.isLandingEntry === true && props.value.showVoiceChat === true && !props.value.selectedOption
  );
  const voiceAiQueryOptions = { enabled: voiceAiQueryEnabled };

  const { data: queuePosition, isLoading: isLoadingQueue } = useQueuePosition(queryOptions);
  const { data: agentsOnline, isLoading: isLoadingAgents } = useAgentsOnline(queryOptions);
  const { data: waitTimes, isLoading: isLoadingWaitTimes } = useWaitTimes(queryOptions);
  const { data: voiceAiAvailability } = useVoiceAiAvailability(voiceAiQueryOptions);

  const connectData = computed<ConnectData>(() => {
    const agentsData: OnlineAgentsCheck = agentsOnline.value || {
      onlineChatAgentsCheck: undefined,
      onlineCallAgentsCheck: undefined,
    };

    const waitTimesData = waitTimes.value || {
      chat: null,
      call: null,
    };

    const isLandingEntry = props.value.isLandingEntry === true;

    const showLiveChat = props.value.showLiveChat && (isLandingEntry || agentsData.onlineChatAgentsCheck !== false);
    const showCall = props.value.showVoiceChat && (isLandingEntry || agentsData.onlineCallAgentsCheck !== false);
    const showOpenRequest = props.value.showOpenRequest;
    const isVoiceAiDisabled =
      isLandingEntry && voiceAiQueryEnabled.value && voiceAiAvailability.value?.available !== true;

    return {
      queuePosition: queuePosition.value || 1,
      agentsOnline: agentsData,
      waitTimes: waitTimesData,
      showLiveChat,
      showCall,
      showOpenRequest,
      isVoiceAiDisabled,
      isLoading: isLandingEntry ? false : isLoadingQueue.value || isLoadingAgents.value || isLoadingWaitTimes.value,
    };
  });

  return connectData;
};
